import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Chip,
  IconButton,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { useMRData } from "./MRDataProvider";
import EditForMR from "../../common/EditForMR";

export default function MRDetailsView({ mr, onClose, onEdit }) {
  const { updateMR } = useMRData();
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";

    // Check if dateString is in the API date format (DD-MMM-YYYY)
    if (typeof dateString === "string" && dateString.includes("-")) {
      return dateString; // Keep API format
    }

    try {
      return dayjs(dateString).format("MMMM D, YYYY");
    } catch (e) {
      return dateString; // Return as is if parsing fails
    }
  };

  // Function to toggle status between Planned and Completed
  const toggleStatus = async () => {
    if (isChangingStatus) return;

    setIsChangingStatus(true);
    const currentStatus = mr.Status || mr.status || "Planned";
    const newStatus = currentStatus === "Planned" ? "Completed" : "Planned";

    try {
      await updateMR(mr.ID || mr.id, { status: newStatus });
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Planned":
        return "#1976d2"; // blue
      case "Completed":
        return "#2e7d32"; // green
      default:
        return "#757575"; // gray
    }
  };

  if (!mr) return null;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          bgcolor: "white",
          zIndex: 1,
          borderBottom: "1px solid #e0e0e0",
          mb: 2,
        }}
      >
        <Typography variant="h5">Management Review Details</Typography>
        {/* <Box mb={2}>
          <EditForMR
            variant="contained"
            //icon={<EditIcon />}
            onClick={onEdit}
          />
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box> */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <EditForMR
            variant="contained"
            //icon={<EditIcon />}
            onClick={onEdit}
          />
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ p: 1 }}>
        <Grid container spacing={1}>
          {/* Main info section */}
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              <strong>Details</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} mb={4}>
              <Grid item xs={12} sm={6} mr={2}>
                <Typography variant="subtitle2">
                  <strong>Date</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {formatDate(mr.Date_of_this_Management_Review || mr.date)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} mr={2}>
                <Typography variant="subtitle2">
                  <strong>Previous Review Date</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {formatDate(
                    mr.Date_of_previous_Management_Review || mr.previousDate
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">
                  <strong>Status</strong>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Chip
                    label={mr.Status || mr.status || "Planned"}
                    sx={{
                      backgroundColor: getStatusColor(mr.Status || mr.status),
                      color: "white",
                      mr: 4,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} mr={2}>
                <Typography variant="subtitle2">
                  <strong>Note Taker</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {mr.Note_taker_for_this_Management_Review || "Not specified"}
                </Typography>
              </Grid>
            </Grid>
            {/* <Divider sx={{ mb: 2, mt: 2 }} /> */}
            <Typography variant="h6" gutterBottom>
              <strong>Review Items Section 1</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} mb={4}>
              <Grid item xs={12} sm={4} mr={10}>
                <Typography variant="subtitle2">
                  <strong>Item A</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.a1 || "Not specified"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} mr={10}>
                <Typography variant="subtitle2">
                  <strong>Item B</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.b1 || "Not specified"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} mr={10}>
                <Typography variant="subtitle2">
                  <strong>Item C</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.c1 || "Not specified"}
                </Typography>
              </Grid>
            </Grid>
            {/* <Divider sx={{ mb: 2, mt: 2 }} /> */}
            <Typography variant="h6" gutterBottom>
              <strong>Review Items Section 2</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={4} mr={13}>
                <Typography variant="subtitle2">
                  <strong>Business Performance</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.a2 || "Not specified"}
                </Typography>
              </Grid>
              <Grid item xs={4} mr={14}>
                <Typography variant="subtitle2">
                  <strong>Internal Audit Results</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.b2 || "Not specified"}
                </Typography>
              </Grid>
              <Grid item xs={4} mr={20}>
                <Typography variant="subtitle2">
                  <strong>Supplier Performance</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.c2 || "Not specified"}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={5}>
              <Grid item xs={4} mr={15}>
                <Typography variant="subtitle2">
                  <strong>Resource Adequacy</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.d2 || "Not specified"}
                </Typography>
              </Grid>
              <Grid item xs={4} mr={15}>
                <Typography variant="subtitle2">
                  <strong>Maintenance Issues</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.e2 || "Not specified"}
                </Typography>
              </Grid>
              <Grid item xs={4} mr={20}>
                <Typography variant="subtitle2">
                  <strong>Customer Feedback</strong>
                </Typography>
                <Typography variant="body1">
                  {mr.f2 || "Not specified"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Side panel */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              <strong>Additional Items</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2">
              <strong>Risks</strong>{" "}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {mr.a3 || "Not specified"}
            </Typography>

            <Typography variant="subtitle2">
              <strong>Opportunities</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {mr.a4 || "Not specified"}
            </Typography>

            <Typography variant="subtitle2">
              <strong>Business Updates</strong>
            </Typography>
            <Typography variant="body1">{mr.a5 || "Not specified"}</Typography>

            <Typography variant="h6" gutterBottom mt={6.3}>
              <strong>Action Items Recap</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2">
              <strong>Item A</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {mr.a6 || "Not specified"}
            </Typography>

            <Typography variant="subtitle2">
              <strong>Item B</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {mr.b6 || "Not specified"}
            </Typography>

            <Typography variant="subtitle2">
              <strong>Item C</strong>
            </Typography>
            <Typography variant="body1">{mr.c6 || "Not specified"}</Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
